import { showTagModal } from '@/components/cashflow/tags/TagModal';
import { useUpdateTags } from '@/components/cashflow/useUpdateTags';
import { MenuButton } from '@/components/common/Button';
import { FlexRow } from '@/components/common/Flex';
import { Plus, Tag01 } from '@/components/common/icons';
import { groupHoverStyleProps } from '@/components/common/styleHelpers';
import { useTracking } from '@/services/analytics/useTracking';
import {
    Box,
    Circle,
    DarkMode,
    LightMode,
    Menu,
    MenuDivider,
    MenuItem,
    MenuItemOption,
    MenuList,
    MenuOptionGroup,
    Portal,
    Text,
    Tooltip,
} from '@chakra-ui/react';
import { CashflowTransactionEntry, useTags } from '@common/react/cashflow';
import { useState } from 'react';

interface TagSelectProps {
    transactionEntry: CashflowTransactionEntry;
    onOpen: () => void;
    onClose: () => void;
}

const TagSelect = ({ transactionEntry, onOpen, onClose }: TagSelectProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const { tags: allTags } = useTags();
    const updateTags = useUpdateTags(transactionEntry);
    const { track } = useTracking();

    const handleChange = async (tagNames: string[]) => {
        track('Tags Updated', { tag_names: tagNames, location: 'TagSelect' });
        await updateTags({
            variables: {
                transactionEntryId: transactionEntry.id,
                tagNames,
            },
        });
    };

    return (
        <DarkMode>
            <Menu
                placement="bottom-start"
                closeOnSelect={false}
                isLazy
                onOpen={() => {
                    setIsOpen(true);
                    onOpen();
                }}
                onClose={() => {
                    setIsOpen(false);
                    onClose();
                }}
                isOpen={isOpen}
            >
                <LightMode>
                    <Tooltip label="Update tags">
                        <MenuButton
                            as={Box}
                            mx={5}
                            visibility={isOpen ? 'visible' : 'hidden'}
                            {...groupHoverStyleProps({
                                visibility: 'visible',
                            })}
                            color="gray.500"
                            _hover={{ color: 'blue.500' }}
                            onClickEvent="Tag Select Button Clicked"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Box>
                                <Tag01 color="inherit" />
                            </Box>
                        </MenuButton>
                    </Tooltip>
                </LightMode>
                <Portal>
                    <MenuList zIndex={2000}>
                        <MenuOptionGroup
                            title="Tags"
                            type="checkbox"
                            onChange={async (tagNames) => {
                                await handleChange(tagNames as string[]);
                            }}
                            defaultValue={transactionEntry.tags.map(
                                (tag) => tag.name
                            )}
                        >
                            {allTags.map((tag) => (
                                <MenuItemOption value={tag.name} key={tag.name}>
                                    <FlexRow
                                        gap={2}
                                        align="center"
                                        justify="flex-start"
                                    >
                                        <Circle bg={tag.color} size={3} />
                                        <Text>{tag.name}</Text>
                                    </FlexRow>
                                </MenuItemOption>
                            ))}
                        </MenuOptionGroup>
                        <MenuDivider />
                        <MenuItem
                            icon={<Plus />}
                            onClick={() =>
                                showTagModal({
                                    onSuccess: async (tag) => {
                                        const newTagNames = [
                                            ...transactionEntry.tags.map(
                                                (tag) => tag.name
                                            ),
                                            tag.name,
                                        ];
                                        await handleChange(newTagNames);
                                    },
                                })
                            }
                        >
                            Create new tag
                        </MenuItem>
                    </MenuList>
                </Portal>
            </Menu>
        </DarkMode>
    );
};

export default TagSelect;
